import { useId } from 'react'
import { ChevronDown, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/shared/config/languages'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

export type ComplianceDocumentGenerateTranslationPrefix =
  | 'riskAssessmentPage'
  | 'manualGenerationPage'

export type ComplianceDocumentGenerateMenuProps = {
  isRegenerate: boolean
  /** When true, show only language + full-document generate (no dropdown). */
  fullDocumentOnly?: boolean
  /** When false, omit the generation language picker (e.g. PPWR assessments). */
  showLanguagePicker?: boolean
  size?: 'default' | 'sm'
  className?: string
  withFileIcon?: boolean
  translationNamespace?: 'machineryDirectiveCompliance'
  translationKeyPrefix: ComplianceDocumentGenerateTranslationPrefix | string
  generationLanguage: SupportedLanguage
  onGenerationLanguageChange: (language: SupportedLanguage) => void
  onGenerate: (payload: {
    language?: SupportedLanguage
    fullDocument?: boolean
  }) => void
  isGeneratePending: boolean
  isExportPending?: boolean
}

type GenerateContentLanguageRadioGroupProps = {
  value: SupportedLanguage
  onValueChange: (language: SupportedLanguage) => void
  label: string
  disabled: boolean
  className?: string
}

function GenerateContentLanguageRadioGroup({
  value,
  onValueChange,
  label,
  disabled,
  className,
}: GenerateContentLanguageRadioGroupProps) {
  const radioGroupId = useId()

  return (
    <RadioGroup
      value={value}
      onValueChange={(nextValue) => {
        onValueChange(nextValue as SupportedLanguage)
      }}
      disabled={disabled}
      aria-label={label}
      className={cn('flex items-center gap-3', className)}
    >
      {SUPPORTED_LANGUAGES.map((language) => {
        const itemId = `${radioGroupId}-${language.value}`
        return (
          <div key={language.value} className='flex items-center gap-2'>
            <RadioGroupItem value={language.value} id={itemId} />
            <Label
              htmlFor={itemId}
              className='flex cursor-pointer items-center gap-2 text-sm font-normal'
            >
              <span>{language.flag}</span>
              <span>{language.label}</span>
            </Label>
          </div>
        )
      })}
    </RadioGroup>
  )
}

/**
 * Generate / regenerate menu for versioned compliance documents (RA, IH).
 *
 * Product rules — generation language picker:
 * - Shown for first-time full document generation (`fullDocumentOnly`).
 * - Shown for full document re-generation (regenerate dropdown).
 * - Not used for per-section regenerate (triggered from each section's menu).
 */
export function ComplianceDocumentGenerateMenu({
  isRegenerate,
  fullDocumentOnly = false,
  showLanguagePicker = true,
  size = 'default',
  className,
  withFileIcon,
  translationNamespace = 'machineryDirectiveCompliance',
  translationKeyPrefix,
  generationLanguage,
  onGenerationLanguageChange,
  onGenerate,
  isGeneratePending,
  isExportPending = false,
}: ComplianceDocumentGenerateMenuProps) {
  const { t } = useTranslation(translationNamespace)
  const labelKey = isRegenerate
    ? `${translationKeyPrefix}.regenerateDocument`
    : `${translationKeyPrefix}.generateDocument`
  const triggerDisabled = isGeneratePending || isExportPending
  const fullDocumentActionLabel = t(
    isRegenerate
      ? `${translationKeyPrefix}.fullDocumentRegenerate`
      : `${translationKeyPrefix}.fullDocumentGenerate`
  )

  if (fullDocumentOnly) {
    return (
      <div className={cn('flex w-full flex-col items-center gap-3', className)}>
        <GenerateContentLanguageRadioGroup
          value={generationLanguage}
          onValueChange={onGenerationLanguageChange}
          label={t('generateContentLanguage')}
          disabled={triggerDisabled}
          className='w-full justify-center'
        />
        <Button
          type='button'
          size={size}
          variant='default'
          className='inline-flex items-center gap-2'
          disabled={triggerDisabled}
          onClick={() => {
            onGenerate({
              fullDocument: true,
              language: generationLanguage,
            })
          }}
        >
          {withFileIcon ? <FileText className='size-4' /> : null}
          {fullDocumentActionLabel}
        </Button>
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          size={size}
          variant={isRegenerate ? 'outline' : 'default'}
          className={cn('inline-flex items-center gap-2', className)}
          disabled={triggerDisabled}
        >
          {withFileIcon ? <FileText className='size-4' /> : null}
          {t(labelKey)}
          <ChevronDown className='size-4 opacity-70' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='w-[min(calc(100vw-2rem),28rem)] p-1'
      >
        {showLanguagePicker ? (
          <>
            <DropdownMenuLabel className='max-w-none whitespace-normal'>
              {t('generateContentLanguage')}
            </DropdownMenuLabel>
            <div className='px-1 pb-2'>
              <GenerateContentLanguageRadioGroup
                value={generationLanguage}
                onValueChange={onGenerationLanguageChange}
                label={t('generateContentLanguage')}
                disabled={triggerDisabled}
                className='w-full'
              />
            </div>
          </>
        ) : null}
        <DropdownMenuItem
          className='cursor-pointer'
          disabled={triggerDisabled}
          onSelect={() => {
            onGenerate({
              fullDocument: true,
              language: generationLanguage,
            })
          }}
        >
          {fullDocumentActionLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
